import { api } from '@/lib/api';

/**
 * V4.9.10 — Admin Reports API client.
 *
 * All endpoints are admin-only — the backend returns 403 for non-admin
 * callers. The ReportsPage gates access at the route level too so
 * non-admin users never see the link in the sidebar.
 */

export interface CategoryStatRow {
  category: string;
  total: number;
  completed: number;
  cancelled: number;
  breached: number;
  avg_resolution_hours: number | null;
}

export interface FaultStatRow {
  fault: string;
  total: number;
  pct_of_total: number;
  top_categories: { category: string; count: number }[];
}

export interface RepeatOffenderRow {
  user_id: string;
  name: string;
  alias: string;
  role: 'solver' | 'communicator' | 'owner';
  fault_tickets: number;
  fault_breakdown: { fault: string; count: number }[];
  top_categories: { category: string; count: number }[];
}

export interface ReportsQuery {
  from?: string;
  to?: string;
  category?: string[];
  fault?: string[];
}

/* V4.9.19 — manual multi-party fault marks, aggregated by person + team. */
export interface FaultPersonRow {
  name: string;
  team: string;
  team_label: string;
  tickets: number;
}
export interface FaultTeamStatRow {
  team: string;
  team_label: string;
  tickets: number;
  marks: number;
}
export interface FaultPeopleReport {
  people: FaultPersonRow[];
  teams: FaultTeamStatRow[];
  total_marked_tickets: number;
  total_marks: number;
}

/* V4.9.18 — one analysed ticket's detail, carried so the clickable bars can
   drill straight into the exact tickets (order #, customer, handlers). */
export interface InsightTicketDetail {
  id: string;
  order_number: string | null;
  customer_name: string | null;
  status: string;
  fault_attribution: string | null;
  culprit_label: string;
  owner: string | null;
  solver: string | null;
  communicator: string | null;
  root_cause_preview: string;
  created_at: string | null;
}

/* V4.9.12 — AI category insight report (mirrors backend CategoryInsight). */
export interface CategoryInsight {
  category: string;
  category_label: string;
  tickets_analyzed: number;
  range: { from?: string; to?: string };
  generated_at: string;
  model: string;
  empty?: boolean;
  truncated?: boolean;
  executive_summary: string;
  // V4.9.18 — each group carries the exact ticket_ids it covers (clickable).
  root_causes: { cause: string; count: number; share_pct: number; ticket_ids: string[] }[];
  fault_breakdown: { party: string; count: number; share_pct: number; ticket_ids: string[] }[];
  key_patterns: string[];
  recommendations: string[];
  tickets: InsightTicketDetail[];
}

export interface InsightQuery {
  category: string;
  from?: string;
  to?: string;
}

/* V4.9.14 — Detailed per-ticket report (mirrors backend). */
export interface DetailCategoryRow {
  category: string;
  category_label: string;
  total: number;
  open: number;
  completed: number;
  cancelled: number;
  breached: number;
  fault_attributed: number;
}

export interface DetailTicketRow {
  id: string;
  category: string;
  status: string;
  severity: string;
  store_code: string;
  customer_name: string | null;
  order_number: string | null;
  owner: string | null;
  solver: string | null;
  communicator: string | null;
  fault_attribution: string | null;
  /** Where the culprit came from: 'ai' | 'manual' | null. V4.9.16. */
  fault_attribution_source: string | null;
  culprit_label: string;
  root_cause_preview: string;
  breached: boolean;
  created_at: string | null;
  resolved_at: string | null;
}

export interface TicketReport {
  id: string;
  category: string;
  category_label: string;
  status: string;
  severity: string;
  store_code: string;
  order_number: string | null;
  customer_name: string | null;
  customer_email: string | null;
  owner: string | null;
  solver: string | null;
  communicator: string | null;
  raised_by: string | null;
  fault_attribution: string | null;
  culprit_label: string;
  culprit_set: boolean;
  root_cause: string;
  summary: string | null;
  requested_action: string | null;
  resolution_notes: string | null;
  outcome: string | null;
  notes: string[];
  tracking_number: string | null;
  quotation_type: string | null;
  breached: boolean;
  created_at: string | null;
  resolved_at: string | null;
}

export interface WeeklyReport {
  range: { from: string; to: string };
  generated_at: string;
  totals: {
    created: number;
    resolved: number;
    cancelled: number;
    breached: number;
    open_now: number;
    fault_attributed: number;
  };
  by_category: DetailCategoryRow[];
  by_fault: { fault: string; fault_label: string; count: number; pct: number }[];
  top_culprits: { name: string; role: string; fault_tickets: number }[];
  notable_tickets: DetailTicketRow[];
}

export interface WeeklySendResult {
  recipient: string;
  delivered: string;
  totals: WeeklyReport['totals'];
}

function buildInsightQS(q: InsightQuery): string {
  const params = new URLSearchParams();
  params.set('category', q.category);
  if (q.from) params.set('from', q.from);
  if (q.to) params.set('to', q.to);
  return `?${params.toString()}`;
}

function buildQS(q: ReportsQuery): string {
  const params = new URLSearchParams();
  if (q.from) params.set('from', q.from);
  if (q.to) params.set('to', q.to);
  if (q.category?.length) params.set('category', q.category.join(','));
  if (q.fault?.length) params.set('fault', q.fault.join(','));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const reportsApi = {
  byCategory: (q: ReportsQuery) =>
    api.get<CategoryStatRow[]>(`/reports/by-category${buildQS(q)}`),
  byFault: (q: ReportsQuery) => api.get<FaultStatRow[]>(`/reports/by-fault${buildQS(q)}`),
  repeatOffenders: (q: ReportsQuery) =>
    api.get<RepeatOffenderRow[]>(`/reports/repeat-offenders${buildQS(q)}`),
  /** V4.9.19 — people manually marked at fault (Solver/Admin marks), by person + team. */
  faultPeople: (q: ReportsQuery) =>
    api.get<FaultPeopleReport>(`/reports/fault-people${buildQS(q)}`),
  /**
   * Downloads the CSV export as a Blob. Goes through the shared axios
   * client (`api.get`) so it inherits the correct baseURL (works in
   * split-origin deploys where VITE_API_URL points at another host),
   * the Bearer-token request interceptor, AND the transparent 401
   * refresh-and-retry. A hand-rolled fetch() to a hardcoded "/api/v1/…"
   * path would break all three. The caller turns the Blob into a
   * download via URL.createObjectURL.
   */
  exportCsv: (q: ReportsQuery) =>
    api.get<Blob>(`/reports/export.csv${buildQS(q)}`, { responseType: 'blob' }),

  /* V4.9.12 — AI category insight. The call can take several seconds (OpenAI),
     so the UI shows a generating state; the backend caches for 30 min. */
  insights: (q: InsightQuery) =>
    api.get<CategoryInsight>(`/reports/insights${buildInsightQS(q)}`, { timeout: 60_000 }),
  /** Export the AI report as a Markdown brief (Blob → download). */
  exportInsightMd: (q: InsightQuery) =>
    api.get<Blob>(`/reports/insights/export.md${buildInsightQS(q)}`, { responseType: 'blob', timeout: 60_000 }),
  /** Export the underlying analysed tickets (notes/resolution) as CSV. */
  exportInsightCsv: (q: InsightQuery) =>
    api.get<Blob>(`/reports/insights/export.csv${buildInsightQS(q)}`, { responseType: 'blob', timeout: 60_000 }),

  /* ===================== V4.9.14 — Detailed per-ticket report ===================== */

  /** Per-category counts for the Detailed tab's clickable cards. */
  detailedCategories: (q: ReportsQuery) =>
    api.get<DetailCategoryRow[]>(`/reports/detailed/categories${buildQS(q)}`),

  /** The drill-down ticket list for one category. */
  detailedTickets: (category: string, q: ReportsQuery) => {
    const params = new URLSearchParams();
    params.set('category', category);
    if (q.from) params.set('from', q.from);
    if (q.to) params.set('to', q.to);
    if (q.fault?.length) params.set('fault', q.fault.join(','));
    return api.get<DetailTicketRow[]>(`/reports/detailed/tickets?${params.toString()}`);
  },

  /** A single ticket's own report — root cause + culprit. */
  ticketReport: (id: string) => api.get<TicketReport>(`/reports/detailed/ticket/${encodeURIComponent(id)}`),

  /** Detailed per-ticket CSV (culprit + full root cause columns). */
  exportDetailedCsv: (q: ReportsQuery) =>
    api.get<Blob>(`/reports/detailed/export.csv${buildQS(q)}`, { responseType: 'blob' }),

  /** Preview the data the weekly email contains (defaults to last 7 days). */
  weeklyPreview: (q: { from?: string; to?: string } = {}) => {
    const params = new URLSearchParams();
    if (q.from) params.set('from', q.from);
    if (q.to) params.set('to', q.to);
    const qs = params.toString();
    return api.get<WeeklyReport>(`/reports/weekly/preview${qs ? `?${qs}` : ''}`);
  },

  /** Send the weekly report email now (to idris@carotechs.com by default). */
  sendWeekly: () => api.post<WeeklySendResult>(`/reports/weekly/send`, {}),

  /* V4.9.15 — AI auto fault attribution. */
  /** How many tickets still have no culprit (drives the button label). */
  unattributedCount: () => api.get<{ count: number }>(`/reports/faults/unattributed-count`),
  /** Run the AI culprit sweep now (fills un-attributed tickets, source='ai'). */
  autoAttributeFaults: () =>
    api.post<{
      attributed: number;
      scanned: number;
      remaining: number;
      aiAvailable: boolean;
      note: string;
    }>(`/reports/faults/auto-attribute`, {}, { timeout: 60_000 })
};
